import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Trash2, Upload } from 'lucide-react';
import { api, extractErrorMessage } from '../../../lib/api';
import { useTenantSettings } from '../../../contexts/TenantSettingsContext';

/**
 * Onglet "Établissement" — US-ADM-01.
 *
 * Champs racine tenant (nom, sous-domaine) + section `general` du settings
 * (adresse, contact) + upload logo (multipart).
 */
export function TenantInfoTab() {
  const { tenant, settings, refresh } = useTenantSettings();
  const [nom, setNom] = useState('');
  const [sousDomaine, setSousDomaine] = useState('');
  const [adresse, setAdresse] = useState('');
  const [telephone, setTelephone] = useState('');
  const [emailGeneral, setEmailGeneral] = useState('');
  const [siteWeb, setSiteWeb] = useState('');
  const [contactNom, setContactNom] = useState('');
  const [contactPrenom, setContactPrenom] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactTel, setContactTel] = useState('');

  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!tenant || !settings) return;
    setNom(tenant.nom);
    setSousDomaine(tenant.sous_domaine ?? '');
    setAdresse(settings.general.adresse);
    setTelephone(settings.general.telephone);
    setEmailGeneral(settings.general.email_general);
    setSiteWeb(settings.general.site_web);
    setContactNom(settings.general.contact_principal.nom);
    setContactPrenom(settings.general.contact_principal.prenom);
    setContactEmail(settings.general.contact_principal.email);
    setContactTel(settings.general.contact_principal.telephone);
  }, [tenant, settings]);

  if (!tenant || !settings) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.patch('/admin/tenant/general', {
        nom,
        sous_domaine: sousDomaine || null,
        general: {
          adresse,
          telephone,
          email_general: emailGeneral,
          site_web: siteWeb,
          contact_principal: {
            nom: contactNom,
            prenom: contactPrenom,
            email: contactEmail,
            telephone: contactTel,
          },
        },
      });
      await refresh();
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2000);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function onLogoUpload(file: File) {
    setUploadingLogo(true);
    setErrorMessage(null);
    try {
      const form = new FormData();
      form.append('logo', file);
      await api.post('/admin/tenant/logo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refresh();
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2000);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setUploadingLogo(false);
    }
  }

  async function onLogoDelete() {
    if (!confirm('Supprimer le logo ?')) return;
    setUploadingLogo(true);
    try {
      await api.delete('/admin/tenant/logo');
      await refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setUploadingLogo(false);
    }
  }

  return (
    <form onSubmit={onSave} className="space-y-6 max-w-3xl">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* Logo */}
      <section className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Logo</h2>
        <div className="flex items-center space-x-4">
          <div className="w-20 h-20 rounded-lg bg-white border border-gray-200 flex items-center justify-center overflow-hidden">
            {tenant.logo_signed_url ? (
              <img
                src={tenant.logo_signed_url}
                alt="Logo"
                className="w-full h-full object-contain"
              />
            ) : (
              <span className="text-xs text-gray-400">aucun</span>
            )}
          </div>
          <div className="flex-1 space-y-2">
            <label className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
              <Upload className="w-4 h-4 mr-2" />
              {uploadingLogo ? 'Upload…' : 'Changer le logo'}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/svg+xml"
                className="hidden"
                disabled={uploadingLogo}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onLogoUpload(f);
                  e.target.value = '';
                }}
              />
            </label>
            {tenant.logo_url && (
              <button
                type="button"
                onClick={onLogoDelete}
                disabled={uploadingLogo}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Supprimer
              </button>
            )}
            <p className="text-xs text-gray-400">
              PNG, JPG, WebP, SVG — 2 Mo max. Redimensionné en 256×256 (sauf SVG vectoriel).
            </p>
          </div>
        </div>
      </section>

      {/* Identité */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Identité</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Nom de l'établissement *">
            <input
              type="text"
              required
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Sous-domaine (optionnel)" hint="ex. acme — utilisé pour acme.mygmao.pro">
            <input
              type="text"
              value={sousDomaine}
              onChange={(e) => setSousDomaine(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Code interne (read-only)">
            <input
              value={tenant.code}
              disabled
              className={inputClass + ' bg-gray-50 text-gray-500'}
            />
          </Field>
        </div>
      </section>

      {/* Coordonnées */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Coordonnées</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Adresse" className="md:col-span-2">
            <textarea
              rows={2}
              value={adresse}
              onChange={(e) => setAdresse(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Téléphone">
            <input
              value={telephone}
              onChange={(e) => setTelephone(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Email général">
            <input
              type="email"
              value={emailGeneral}
              onChange={(e) => setEmailGeneral(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Site web" className="md:col-span-2">
            <input
              type="url"
              value={siteWeb}
              onChange={(e) => setSiteWeb(e.target.value)}
              placeholder="https://"
              className={inputClass}
            />
          </Field>
        </div>
      </section>

      {/* Contact principal */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Contact principal</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Prénom">
            <input
              value={contactPrenom}
              onChange={(e) => setContactPrenom(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Nom">
            <input
              value={contactNom}
              onChange={(e) => setContactNom(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Téléphone">
            <input
              value={contactTel}
              onChange={(e) => setContactTel(e.target.value)}
              className={inputClass}
            />
          </Field>
        </div>
      </section>

      {/* Footer */}
      <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
        {savedAt && (
          <span className="inline-flex items-center text-sm text-green-600">
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Enregistré
          </span>
        )}
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
        >
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Enregistrer
        </button>
      </div>
    </form>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}
